import { Component, input, output } from '@angular/core';
import { ComponentVariant, ComponentSize } from '../types';

@Component({
  selector: 'app-chip',
  imports: [],
  template: `
    <span 
      [class]="chipClasses"
      [attr.aria-label]="label()">
      @if (icon()) {
        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="icon()" />
        </svg>
      }
      <span>{{ label() }}</span>
      @if (removable()) {
        <button
          type="button"
          (click)="onRemove()"
          class="ml-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5 transition-colors"
          [attr.aria-label]="'Remove ' + label()">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      }
    </span>
  `,
  styles: []
})
export class ChipComponent {
  label = input<string>('');
  variant = input<ComponentVariant>('primary');
  size = input<ComponentSize>('md');
  icon = input<string>('');
  removable = input<boolean>(false);
  outlined = input<boolean>(false);

  removeEvent = output<void>();

  onRemove(): void {
    this.removeEvent.emit();
  }

  get chipClasses(): string {
    const classes = ['inline-flex', 'items-center', 'gap-1', 'font-medium', 'whitespace-nowrap'];

    // Size
    const sizeMap = {
      xs: 'text-xs px-1.5 py-0.5',
      sm: 'text-xs px-2 py-1',
      md: 'text-sm px-2.5 py-1',
      lg: 'text-base px-3 py-1.5',
      xl: 'text-lg px-4 py-2'
    };
    classes.push(sizeMap[this.size()]);

    // Rounded
    classes.push('rounded-full');

    // Variant colors
    if (this.outlined()) {
      classes.push('border-2', 'bg-transparent');
      const variantMap = {
        success: 'border-green-500 text-green-700 dark:text-green-400',
        error: 'border-red-500 text-red-700 dark:text-red-400',
        warning: 'border-yellow-500 text-yellow-700 dark:text-yellow-400',
        info: 'border-blue-500 text-blue-700 dark:text-blue-400',
        primary: 'border-blue-600 text-blue-700 dark:text-blue-400',
        secondary: 'border-gray-500 text-gray-700 dark:text-gray-400',
        danger: 'border-red-500 text-red-700 dark:text-red-400'
      };
      classes.push(variantMap[this.variant()] || variantMap.primary);
    } else {
      const variantMap = {
        success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        primary: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        secondary: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
        danger: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      };
      classes.push(variantMap[this.variant()] || variantMap.primary);
    }

    return classes.join(' ');
  }
}

